import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine, Keyboard, ImageUp, CameraOff, CalendarDays, MapPin, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, OUTCOME_CLS } from "@/lib/format";
import { NativeSelect, Badge, Loading } from "@/components/common";
import { Input } from "@/components/ui/input";
import { ScanResult } from "@/components/ScanResult";
import { RosterPanel } from "@/components/RosterPanel";
import { AdmittedCounter } from "@/components/AdmittedCounter";

function unlockAudio(ctxRef) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = ctxRef.current || (ctxRef.current = new Ctx());
    if (ctx.state === "suspended") ctx.resume();
    return ctx;
  } catch { return null; }
}

function tone(ctxRef, ok) {
  try {
    const ctx = unlockAudio(ctxRef);
    if (!ctx) return;
    const seq = ok ? [[880, 0, 0.12], [1320, 0.13, 0.3]] : [[280, 0, 0.22], [200, 0.27, 0.55]];
    seq.forEach(([f, s, e]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = ok ? "sine" : "square";
      o.frequency.value = f;
      g.gain.setValueAtTime(0.2, ctx.currentTime + s);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + e);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + s);
      o.stop(ctx.currentTime + e + 0.02);
    });
    navigator.vibrate?.(ok ? 90 : [140, 80, 140]);
  } catch { /* audio non disponibile */ }
}

export default function Scanner() {
  const [params] = useSearchParams();
  const [events, setEvents] = useState(null);
  const [eventId, setEventId] = useState(params.get("event") || "");
  const [camera, setCamera] = useState("off");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState("");
  const [history, setHistory] = useState([]);
  const [rosterTick, setRosterTick] = useState(0);
  const [counter, setCounter] = useState(null);
  useEffect(() => { setCounter(null); }, [eventId]);
  const onRoster = useCallback((d) => setCounter({ count: d.registered.length, max: d.max_participants || 0 }), []);
  const scanner = useRef(null);
  const lock = useRef(false);
  const audio = useRef(null);
  const code = useRef("");

  useEffect(() => {
    api.get("/events").then((r) => {
      const active = r.data.filter((e) => !["Annullata", "Conclusa"].includes(e.status));
      setEvents(active);
      setEventId((cur) => cur || (active.length === 1 ? active[0].id : ""));
    }).catch(() => setEvents([]));
    return () => { scanner.current?.isScanning && scanner.current.stop().catch(() => {}); };
  }, []);

  const show = useCallback((kind, data, message) => {
    setResult({ kind, data, message });
    tone(audio, kind === "admitted" || kind === "registered");
    if (data?.athlete || kind === "unavailable") {
      setHistory((h) => [{ at: new Date(), name: data?.athlete?.full_name || "—", outcome: { admitted: "AMMESSO", registered: "AMMESSO", denied: "NON AMMESSO", already: "GIÀ REGISTRATA" }[kind] || "NON DISPONIBILE" }, ...h].slice(0, 12));
    }
  }, []);

  const handleError = (e) => {
    if (!e.response || e.response.status >= 500) return show("unavailable", null, "Server non raggiungibile");
    return show("unavailable", null, errMsg(e));
  };

  const verify = useCallback(async (text) => {
    code.current = text;
    setBusy(true);
    try {
      const { data } = await api.post("/verify", { event_id: eventId, code: text });
      show(data.outcome === "AMMESSO" ? "admitted" : data.outcome === "GIÀ REGISTRATA" ? "already" : "denied", data);
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, show]);

  const onDecode = useCallback((text) => {
    if (lock.current) return;
    lock.current = true;
    try { scanner.current?.pause(true); } catch { /* già in pausa */ }
    verify(text);
  }, [verify]);

  const startCamera = async () => {
    unlockAudio(audio);
    setCamera("starting");
    try {
      if (!scanner.current) scanner.current = new Html5Qrcode("qr-reader", { verbose: false });
      await scanner.current.start({ facingMode: "environment" },
        { fps: 12, qrbox: (w, h) => { const s = Math.floor(Math.min(w, h) * 0.72); return { width: s, height: s }; } },
        (t) => onDecode(t), () => {});
      lock.current = false;
      setCamera("on");
    } catch (e) {
      setCamera("error");
    }
  };

  const next = () => {
    setResult(null);
    lock.current = false;
    try { if (scanner.current?.getState?.() === 3) scanner.current.resume(); } catch { /* riavvio */ }
  };

  const register = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/participations", { event_id: eventId, code: code.current });
      setCounter((c) => c && { ...c, count: c.count + 1 });
      show("registered", { ...result.data, reasons: [] }, `Registrata alle ${new Date(data.registered_at).toLocaleTimeString("it-IT")}`);
      setRosterTick((t) => t + 1);
    } catch (e) {
      if (e.response?.status === 409) show("already", { ...result.data, reasons: [] }, errMsg(e));
      else if (e.response?.status === 422) show("denied", { ...result.data, reasons: e.response.data.detail.reasons });
      else handleError(e);
    } finally {
      setBusy(false);
    }
  };

  const scanFile = async (file) => {
    if (!file) return;
    try {
      const reader = new Html5Qrcode("qr-file-reader", { verbose: false });
      const text = await reader.scanFile(file, false);
      reader.clear();
      lock.current = true;
      verify(text);
    } catch {
      show("denied", { reasons: ["QR code non leggibile nell'immagine"] });
    }
  };

  if (events === null) return <Loading />;
  const ev = events.find((e) => e.id === eventId);

  return (
    <div className="-mx-4 -my-6 min-h-[calc(100vh-56px)] bg-slate-950 px-4 py-5 text-white sm:-mx-6 sm:px-6 lg:-mx-10 lg:-my-10 lg:min-h-screen lg:px-10 lg:py-8">
      <div className="mx-auto max-w-xl">
        <div className="eyebrow !text-amber-400">Scanner QR</div>
        <h1 className="mt-1 font-display text-2xl font-black tracking-tight sm:text-3xl" data-testid="page-title">VERIFICA PARTECIPAZIONE ATLETA</h1>
        <div className="mt-4">
          <NativeSelect value={eventId} onChange={setEventId} className="h-12 w-full !border-slate-700 !bg-slate-900 text-base !text-white" testid="scanner-event-select">
            <option value="">Seleziona la manifestazione…</option>
            {events.map((e) => <option key={e.id} value={e.id}>{e.name} · {fmtDate(e.start_date)}</option>)}
          </NativeSelect>
          {events.length === 0 && <div className="mt-2 text-sm text-amber-300">Nessuna manifestazione attiva assegnata al tuo account.</div>}
        </div>
        {ev && counter && <div className="sticky top-16 z-10 mt-3 lg:top-4"><AdmittedCounter count={counter.count} max={counter.max} /></div>}
        {ev && (
          <div className="mt-3 rounded-xl border border-slate-800 bg-slate-900 p-3 text-sm" data-testid="scanner-event-info">
            <div className="font-semibold">{ev.discipline_name} · {ev.allowed_categories.map((c) => c.name).join(", ")}</div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-slate-400">
              <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(ev.start_date)}</span>
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{ev.location}</span>
              <span>{ev.participants_count}{ev.max_participants ? `/${ev.max_participants}` : ""} iscritti</span>
            </div>
          </div>
        )}
        <div className="relative mt-4 aspect-[3/4] max-h-[62vh] w-full overflow-hidden rounded-3xl border border-slate-800 bg-black sm:aspect-square">
          <div id="qr-reader" className="h-full w-full" />
          {camera === "on" && <div className="pointer-events-none absolute inset-x-[14%] h-0.5 bg-emerald-400 shadow-[0_0_16px_4px_rgba(16,185,129,.6)] scan-line" />}
          {camera !== "on" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
              {camera === "error" ? (
                <><CameraOff className="h-12 w-12 text-red-400" /><div className="text-sm text-slate-300" data-testid="camera-error">Fotocamera non disponibile. Concedi il permesso alla fotocamera oppure usa l'inserimento manuale o un'immagine del QR.</div></>
              ) : (
                <button onClick={startCamera} disabled={!eventId || camera === "starting"} data-testid="scan-license-btn"
                  className="flex h-20 w-full max-w-xs items-center justify-center gap-3 rounded-2xl bg-emerald-500 text-xl font-black tracking-wide text-white shadow-[0_10px_40px_-10px_rgba(16,185,129,.8)] transition-transform active:scale-[0.97] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:shadow-none">
                  {camera === "starting" ? <Loader2 className="h-7 w-7 animate-spin" /> : <ScanLine className="h-7 w-7" />}SCANSIONA LICENZA
                </button>
              )}
              {!eventId && <div className="text-sm text-slate-400">Seleziona prima una manifestazione</div>}
            </div>
          )}
          {busy && !result && <div className="absolute inset-0 flex items-center justify-center bg-black/60"><Loader2 className="h-12 w-12 animate-spin text-emerald-400" /></div>}
        </div>
        <div id="qr-file-reader" className="hidden" />
        <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto]">
          <form onSubmit={(e) => { e.preventDefault(); unlockAudio(audio); if (manual.trim() && eventId) { lock.current = true; verify(manual.trim()); } }} className="flex gap-2">
            <div className="relative flex-1">
              <Keyboard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Codice o link del QR" className="h-12 border-slate-700 bg-slate-900 pl-9 text-white placeholder:text-slate-500" data-testid="manual-code-input" />
            </div>
            <button type="submit" disabled={!eventId || !manual.trim()} className="h-12 rounded-xl bg-slate-800 px-4 text-sm font-bold transition-colors hover:bg-slate-700 disabled:opacity-40" data-testid="manual-verify-btn">Verifica</button>
          </form>
          <label className={`flex h-12 cursor-pointer items-center justify-center gap-2 rounded-xl bg-slate-800 px-4 text-sm font-bold transition-colors hover:bg-slate-700 ${!eventId ? "pointer-events-none opacity-40" : ""}`}>
            <ImageUp className="h-4 w-4" />Da immagine
            <input type="file" accept="image/*" className="hidden" onClick={() => unlockAudio(audio)} onChange={(e) => { scanFile(e.target.files[0]); e.target.value = ""; }} data-testid="scan-file-input" />
          </label>
        </div>
        <RosterPanel eventId={eventId} refreshKey={rosterTick} onData={onRoster} />
        {history.length > 0 && (
          <div className="mt-6" data-testid="scan-history">
            <div className="eyebrow mb-2 !text-slate-400">Verifiche di questa sessione</div>
            <div className="divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900">
              {history.map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span className="text-slate-400">{h.at.toLocaleTimeString("it-IT")}</span>
                  <span className="flex-1 truncate">{h.name}</span>
                  <Badge cls={OUTCOME_CLS[h.outcome] || "bg-slate-700 text-white border-slate-600"}>{h.outcome}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {result && <ScanResult result={result} busy={busy} onRegister={register} onNext={next} onClose={next} counter={counter} />}
    </div>
  );
}
